import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Check, Clock, Video } from "lucide-react";

/** YouTube / Vimeo links have to play in an iframe; uploaded files use <video>. */
function embedUrl(url = "") {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return "";
}

export default function YogaLessonPlayer() {
  const { day } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [busy, setBusy] = useState(false);
  const dayNum = parseInt(day, 10);

  useEffect(() => {
    setLesson(null);
    api.get(`/yoga/lessons/${dayNum}`).then(({ data }) => setLesson(data)).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail));
      nav("/yoga/dashboard");
    });
  }, [dayNum, nav]);

  const markDone = async () => {
    setBusy(true);
    try {
      await api.post(`/yoga/lessons/${dayNum}/complete`);
      toast.success("Beautifully done. Namaste.");
      if (dayNum < 21) nav(`/yoga/day/${dayNum + 1}`);
      else nav("/yoga/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!lesson) return <main className="p-12 text-center font-display text-2xl">Loading…</main>;

  return (
    <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
      <Link to="/yoga/dashboard" className="text-sm text-muted-foreground flex items-center gap-1 mb-6 hover:text-primary" data-testid="lesson-back">
        <ArrowLeft size={14}/> Back to dashboard
      </Link>

      <div className="mb-6">
        <div className="overline mb-2">Day {lesson.day} of 21</div>
        <h1 className="font-display text-4xl lg:text-5xl mb-3">{lesson.title}</h1>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Clock size={14}/>{lesson.duration_minutes} min</span>
          <span className="flex items-center gap-1"><Video size={14}/>{lesson.level}</span>
          {lesson.completed && <Badge className="bg-primary text-primary-foreground"><Check size={10} className="mr-0.5"/>Completed</Badge>}
        </div>
      </div>

      <div className="rounded-3xl overflow-hidden bg-black border border-border mb-8">
        {embedUrl(lesson.video_url) ? (
          <iframe
            key={lesson.video_url}
            src={embedUrl(lesson.video_url)}
            title={lesson.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full aspect-video"
            data-testid="lesson-video-embed"
          />
        ) : (
          <video
            key={lesson.video_url}
            controls
            playsInline
            preload="metadata"
            className="w-full aspect-video"
            poster={mediaUrl(lesson.cover_image)}
            data-testid="lesson-video"
          >
            <source src={mediaUrl(lesson.video_url)} />
            Your browser does not support HTML5 video.
          </video>
        )}
      </div>

      {lesson.is_preview && (
        <div className="rounded-2xl border border-secondary/40 bg-secondary/5 p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="lesson-preview-banner">
          <div>
            <div className="font-display text-xl">This is your free Day 1 preview</div>
            <p className="text-sm text-muted-foreground mt-1">
              Join the 21-Day Starter to unlock Day 2 to 21, the weekly live classes and your progress tracker.
            </p>
          </div>
          <Link to="/yoga">
            <Button className="btn-pill bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="lesson-preview-cta">
              Unlock the full course
            </Button>
          </Link>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border rounded-2xl p-6 lg:p-8 bg-card">
          <h3 className="font-display text-2xl mb-3">Today's practice</h3>
          <p className="text-foreground/80 leading-relaxed">{lesson.description}</p>
          <div className="mt-6 border-t border-border pt-6 text-sm text-muted-foreground">
            <b>Tip:</b> Find a quiet corner. Roll out your mat, dim the lights, and take three deep breaths before pressing play.
          </div>
        </div>

        <div className="border border-border rounded-2xl p-6 bg-card h-fit">
          <h4 className="font-display text-xl mb-3">Finish today's session</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Mark this day as complete to unlock <b>Day {Math.min(dayNum + 1, 21)}</b>.
          </p>
          <Button onClick={markDone} disabled={busy} className="w-full btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="lesson-complete-btn">
            {busy ? "Saving…" : lesson.completed ? "Complete Again" : "I've completed today"}
          </Button>
          {dayNum < 21 && lesson.completed && (
            <Link to={`/yoga/day/${dayNum + 1}`}>
              <Button variant="outline" className="w-full btn-pill mt-2" data-testid="lesson-next-btn">
                Day {dayNum + 1} <ArrowRight size={14} className="ml-1"/>
              </Button>
            </Link>
          )}
        </div>
      </div>
    </main>
  );
}
